import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'

const WordTaskDetail = ({ userData, onBack, wordTaskId, wordTaskTitle }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [wordTaskDetails, setWordTaskDetails] = useState(null)
  const [studentResults, setStudentResults] = useState([])
  const [statistics, setStatistics] = useState({
    totalStudents: 0,
    completedStudents: 0,
    averageScore: 0
  })

  useEffect(() => {
    if (user && wordTaskId) {
      loadWordTaskDetails()
      loadStudentResults()
    }
  }, [user, wordTaskId])

  const loadWordTaskDetails = async () => {
    try {
      const wordTaskRef = doc(db, 'wordTasks', wordTaskId)
      const wordTaskDoc = await getDoc(wordTaskRef)
      
      if (wordTaskDoc.exists()) {
        const data = wordTaskDoc.data()
        setWordTaskDetails({
          id: wordTaskDoc.id,
          title: data.title || 'Bilinmeyen Görev',
          description: data.description || '',
          taskMode: data.taskMode || 'study',
          className: data.className || '',
          createdDate: data.createdDate?.toDate() || new Date(),
          studentIds: data.studentIds || [],
          words: data.words || []
        })
      } else {
        toast.error('Görev bulunamadı')
        onBack()
      }
    } catch (error) {
      console.error('Error loading word task details:', error)
      toast.error('Görev detayları yüklenirken hata oluştu')
    }
  }

  const loadStudentResults = async () => {
    try {
      const resultsRef = collection(db, 'wordTaskResults')
      const q = query(
        resultsRef,
        where('wordTaskId', '==', wordTaskId),
        where('teacherId', '==', user.uid)
      )
      const querySnapshot = await getDocs(q)
      
      const results = []
      const studentResultsMap = new Map()
      
      // Her öğrenci için sadece en son sonucu al
      for (const resultDoc of querySnapshot.docs) {
        const resultData = resultDoc.data()
        const studentId = resultData.studentId
        const score = resultData.score || 0
        const totalQuestions = resultData.totalQuestions || 0
        const completionDate = resultData.completionDate?.toDate() || new Date()
        const taskMode = resultData.taskMode || 'study'
        
        // Eğer bu öğrenci için daha önce sonuç varsa, sadece en son olanı al
        if (studentResultsMap.has(studentId)) {
          const existingResult = studentResultsMap.get(studentId)
          if (completionDate > existingResult.completionDate) {
            existingResult.score = score
            existingResult.totalQuestions = totalQuestions
            existingResult.completionDate = completionDate
            existingResult.taskMode = taskMode
          }
        } else {
          // İlk kez bu öğrenci için sonuç
          const studentResult = {
            studentId: studentId,
            score: score,
            totalQuestions: totalQuestions,
            completionDate: completionDate,
            taskMode: taskMode,
            studentName: '',
            successRate: totalQuestions > 0 ? (score / totalQuestions) * 100 : 0
          }
          studentResultsMap.set(studentId, studentResult)
        }
      }
      
      // Öğrenci isimlerini al
      for (const [studentId, studentResult] of studentResultsMap) {
        try {
          const userDoc = await getDoc(doc(db, 'users', studentId))
          if (userDoc.exists()) {
            studentResult.studentName = userDoc.data().username || 'Bilinmeyen Öğrenci'
          }
        } catch (error) {
          console.error('Error getting student name:', error)
          studentResult.studentName = 'Bilinmeyen Öğrenci'
        }
      }
      
      const finalResults = Array.from(studentResultsMap.values())
      
      // Başarı oranına göre sırala (en yüksek en üstte)
      finalResults.sort((a, b) => b.successRate - a.successRate)
      
      setStudentResults(finalResults)
      
      // İstatistikleri hesapla
      const totalStudents = wordTaskDetails?.studentIds?.length || 0
      const completedStudents = finalResults.length
      const totalScore = finalResults.reduce((sum, result) => sum + result.score, 0)
      const totalQuestions = finalResults.reduce((sum, result) => sum + result.totalQuestions, 0)
      const averageScore = totalQuestions > 0 ? (totalScore / totalQuestions) * 100 : 0
      
      setStatistics({
        totalStudents,
        completedStudents,
        averageScore
      })
      
    } catch (error) {
      console.error('Error loading student results:', error)
      toast.error('Öğrenci sonuçları yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const getModeText = (mode) => {
    switch (mode) {
      case 'matching':
        return '🎯 Eşleştirme Oyunu'
      case 'meaning':
        return '❓ Anlam Sorma'
      default:
        return '📚 Çalışma Modu'
    }
  }

  const getModeColor = (mode) => {
    switch (mode) {
      case 'matching':
        return 'warning'
      case 'meaning':
        return 'info'
      default:
        return 'primary'
    }
  }

  const getSuccessRateColor = (rate) => {
    if (rate >= 80) return 'success'
    if (rate >= 60) return 'warning'
    return 'error'
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Görev Detayları
            </Typography>
          </Toolbar>
        </AppBar>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    )
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Görev Detayları
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Task Info Card */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
              {wordTaskDetails?.title}
            </Typography>
            
            {wordTaskDetails?.description && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {wordTaskDetails.description}
              </Typography>
            )}

            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Chip
                label={getModeText(wordTaskDetails?.taskMode)}
                color={getModeColor(wordTaskDetails?.taskMode)}
                size="small"
                variant="outlined"
              />
              <Typography variant="body2" color="text.secondary">
                •
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {wordTaskDetails?.className || '-'}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              {formatDate(wordTaskDetails?.createdDate)}
            </Typography>
          </CardContent>
        </Card>

        {/* Statistics Card */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              📊 İstatistikler
            </Typography>
            
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2" color="text.secondary">
                Toplam {statistics.totalStudents} öğrenci
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {statistics.completedStudents} öğrenci tamamladı
              </Typography>
            </Box>
            
            <Typography variant="h6" fontWeight="bold" color="success.main">
              Ortalama başarı: %{statistics.averageScore.toFixed(1)}
            </Typography>
          </CardContent>
        </Card>

        {/* Student Results */}
        <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
          👥 Öğrenci Sonuçları
        </Typography>

        {studentResults.length === 0 ? (
          <Alert severity="info" sx={{ textAlign: 'center' }}>
            Henüz öğrenci sonucu bulunmuyor
          </Alert>
        ) : (
          <List>
            {studentResults.map((result, index) => (
              <Card key={result.studentId} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {result.studentName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {result.score}/{result.totalQuestions} doğru
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box textAlign="right">
                      <Chip
                        label={`%${result.successRate.toFixed(1)}`}
                        color={getSuccessRateColor(result.successRate)}
                        size="small"
                        variant="outlined"
                      />
                      <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                        {formatDate(result.completionDate)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </List>
        )}
      </Container>
      </Box>
    </SpaceTheme>
  )
}

export default WordTaskDetail
